import { useCallback, useRef, useState } from "react";
import { AmountField } from "../components/AmountField";
import { Button } from "../components/Button";
import { Callout } from "../components/Callout";
import { Card } from "../components/Card";
import { CountdownDisplay } from "../components/CountdownDisplay";
import { Skeleton } from "../components/Skeleton";
import { VisuallyHidden } from "../components/VisuallyHidden";
import { ApiError } from "../api/client";
import { useCountdown } from "../hooks/useCountdown";
import { messageForBidError } from "../lib/bid-messages";
import { formatDisplayDateLong, formatMoney } from "../lib/format";
import { parseBidAmount } from "../lib/money";
import styles from "./AuctionPanel.module.css";
import type { FormEvent } from "react";
import type { PlacedBid, SellerAuctionDeal, SellerAuctionView } from "../api/types";

/**
 * Tomorrow's auction, and the one control that changes it (canon APP-003).
 *
 * The panel is built around the fact founders most easily misread: **today's bid buys
 * tomorrow**. Canon § 7 makes each daily auction independent and settles it for the next
 * display day, so the date, the countdown, the button's own confirmation and the closing
 * note all say the same thing rather than leaving it to be inferred from the word
 * "tomorrow" in a heading.
 *
 * Everything numeric here is core's. The projected rank, the cut line, the auction's daily
 * minimum and each deal's next accepted amount are read from the response and rendered
 * (`contracts/API.md` § Seller bidding shape); nothing on this page recomputes a rank or a
 * balance, because `docs/SECURITY.md` § Credits/bids makes the browser's copy display-only
 * and the server recomputes the same sums when it accepts a bid.
 *
 * Canon § 12's sponsored disclosure travels in the response and is shown verbatim: money
 * buys placement, not standing, and the founder spending it is told so in the same words a
 * buyer sees.
 */

export function AuctionSkeleton() {
  return (
    <Card className={styles.panel}>
      <Skeleton width="14rem" height="2rem" />
      <Skeleton width="60%" height="1rem" />
      <Skeleton width="100%" height="4rem" />
    </Card>
  );
}

export interface AuctionPanelProps {
  view: SellerAuctionView;
  /** The local instant this view arrived, so the countdown can use core's clock. */
  receivedAtMs: number;
  /**
   * Available promotional credit from the credit panel's own read, or `null` while that
   * read is loading or failed. It is used only to state what a bid would leave — never to
   * decide whether a bid may be sent, which is core's call alone.
   */
  availableCents: number | null;
  placeBid: (input: { dealId: string; amountCents: number }) => Promise<PlacedBid>;
  /** Called after core accepts a bid, so the desk can re-read the auction and the balance. */
  onBidAccepted: () => void;
}

/**
 * "eleven" rather than "11", in running prose.
 *
 * The threshold is canon's to change (canon § 6), so the copy reads it from the response
 * rather than hard-coding a number that would quietly become a lie. It is stated as the
 * condition that opens the auction and never as places remaining: `AGENTS.md` § 11 and
 * decision `D-009` forbid fake scarcity, and a countdown is exactly that.
 */
function cardinal(value: number): string {
  const names = [
    "zero", "one", "two", "three", "four", "five",
    "six", "seven", "eight", "nine", "ten", "eleven",
  ];
  return names[value] ?? String(value);
}

export function AuctionPanel({
  view,
  receivedAtMs,
  availableCents,
  placeBid,
  onBidAccepted,
}: AuctionPanelProps) {
  const auction = view.auction;

  // Only an open auction has a deadline worth counting down to. A finalizing or finalized
  // one has already closed, and a countdown there would be theatre.
  const countdown = useCountdown(
    auction !== null && auction.status === "open" ? auction.closesAt : null,
    view.serverTime,
    receivedAtMs,
  );

  const closesOn = `00:00 UTC on ${formatDisplayDateLong(view.displayDate)}`;

  // Bidding is open when core says the auction is open *and* the deadline has not passed on
  // this screen. The countdown is measured against core's clock, so this closes the form at
  // the same instant core starts refusing — rather than leaving a stale form up until the
  // next poll and letting a founder press a button that cannot work.
  // Canon § 6 (decision `D-011`) is the outermost condition: below eleven eligible deals no
  // auction runs at all, so an open auction row is not on its own permission to bid.
  const biddingOpen =
    view.bidding.open &&
    auction !== null &&
    auction.status === "open" &&
    countdown?.expired !== true;

  if (!view.bidding.open) {
    return (
      <Callout title="Bidding hasn't started yet">
        <p className={styles.coldStart}>
          While Dealbid has {view.bidding.eligibleDealsRequired - 1} deals or fewer, every
          approved deal holds a place on the board in the order it was approved — yours
          included, at no cost.
        </p>
        <p className={styles.coldStart}>
          The daily auction opens once Dealbid has{" "}
          {cardinal(view.bidding.eligibleDealsRequired)} approved deals. Nothing is required
          from you until then.
        </p>
      </Callout>
    );
  }

  if (auction === null) {
    return (
      <Callout title="Tomorrow's auction hasn't opened yet">
        A new auction opens when today&rsquo;s board is finalised. Bids for{" "}
        {formatDisplayDateLong(view.displayDate)} can be placed from then on.
      </Callout>
    );
  }

  return (
    <div className={styles.panel}>
      <Card tone="lead" className={styles.status}>
        {auction.status === "open" && countdown !== null ? (
          <CountdownDisplay
            label="Bidding closes in"
            countdown={countdown}
            absolute={closesOn}
            expiredLabel="Bidding has closed. Tomorrow's board is being finalised."
          />
        ) : (
          <p className={styles.closed}>
            Bidding is closed while tomorrow&rsquo;s board is finalised.
          </p>
        )}

        <p className={styles.terms}>
          Bidding starts at <span className="figure">{formatMoney(auction.minBidCents)}</span>.{" "}
          <span className="figure">{String(auction.capacity.standingBids)}</span>{" "}
          {auction.capacity.standingBids === 1 ? "bid is" : "bids are"} competing for{" "}
          <span className="figure">{String(auction.capacity.top10)}</span> positions on{" "}
          {formatDisplayDateLong(view.displayDate)}.
        </p>

        <p className={styles.available}>
          {availableCents === null ? (
            "Your available promotional credit is on the panel below."
          ) : (
            <>
              You have <span className="figure">{formatMoney(availableCents)}</span> of
              promotional credit available to bid with.{" "}
              {/* Canon § 5 and § 17: paying the difference on a larger bid needs a live
                  provider, and there is none yet. Saying so here is what keeps the refusal
                  from arriving as a surprise after the founder has typed an amount. */}
              Bids above your balance are not available yet.
            </>
          )}
        </p>

        {/* Canon § 12: bid-driven placement is disclosed as sponsored in core's own words,
            so the founder buying it reads the same sentence the buyer does. */}
        <p className={styles.disclosure}>{view.disclosure.message}</p>
      </Card>

      {view.deals.length === 0 ? (
        <Callout title="No deal of yours can bid yet">
          Only an approved deal can bid for a Top 10 position. Where each of your deals stands
          in review is on the panel below.
        </Callout>
      ) : (
        <ul className={styles.deals} role="list">
          {view.deals.map((deal) => (
            <li key={deal.dealId}>
              <DealBidCard
                deal={deal}
                displayDate={view.displayDate}
                top10Capacity={auction.capacity.top10}
                biddingOpen={biddingOpen}
                availableCents={availableCents}
                placeBid={placeBid}
                onBidAccepted={onBidAccepted}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * One approved deal, its standing bid and its bid control.
 *
 * The rank is stated in words as well as drawn as a mark, and whether the position is
 * winning is stated in a sentence rather than carried by the mark's colour — canon
 * `contracts/DESIGN-TOKENS.md` does not allow status by colour alone, and "position 11" and
 * "position 10" are the difference between a placement and nothing.
 */
function DealBidCard({
  deal,
  displayDate,
  top10Capacity,
  biddingOpen,
  availableCents,
  placeBid,
  onBidAccepted,
}: {
  deal: SellerAuctionDeal;
  displayDate: string;
  top10Capacity: number;
  biddingOpen: boolean;
  availableCents: number | null;
  placeBid: AuctionPanelProps["placeBid"];
  onBidAccepted: () => void;
}) {
  const bid = deal.bid;

  return (
    <Card as="article" className={styles.deal}>
      <div className={styles.head}>
        <div className={styles.mark} aria-hidden="true">
          {bid === null ? "—" : `#${String(bid.projectedRank)}`}
        </div>
        <div className={styles.names}>
          <h3 className={styles.product}>{deal.productName}</h3>
          {bid === null ? (
            <p className={styles.standing}>
              No bid yet for {formatDisplayDateLong(displayDate)}.
            </p>
          ) : (
            <p className={styles.standing}>
              Your standing bid{" "}
              <span className="figure">{formatMoney(bid.amountCents)}</span> ·
              position <span className="figure">{String(bid.projectedRank)}</span> as things
              stand
            </p>
          )}
        </div>
      </div>

      {bid === null ? null : (
        <p className={bid.withinTop10 ? styles.winning : styles.losing}>
          {bid.withinTop10
            ? `Inside tomorrow's Top ${String(top10Capacity)} as things stand. Other founders can still outbid this before bidding closes.`
            : `Outside tomorrow's Top ${String(top10Capacity)} as things stand. Raising this bid is the only way to move up.`}
        </p>
      )}

      <BidForm
        deal={deal}
        displayDate={displayDate}
        biddingOpen={biddingOpen}
        availableCents={availableCents}
        placeBid={placeBid}
        onBidAccepted={onBidAccepted}
      />
    </Card>
  );
}

type BidFormStatus = "idle" | "submitting" | "success" | "error";

/**
 * The bid control for one deal.
 *
 * The local checks exist to answer quickly, never to be authoritative. Core validates the
 * amount again, applies the auction's own minimum, decides whether the credit is there and
 * whether the deal may bid at all — and when it disagrees, its refusal is what the founder
 * is shown. Nothing here is relaxed on the assumption that "the server will catch it"
 * either: a form that sends an amount it already knows is below the minimum spends a round
 * trip to say what it could have said immediately.
 *
 * A second submit while one is in flight is ignored. A founder pressing the button twice
 * must not place two bids, and canon § 7 gives a deal at most one standing bid per auction.
 */
function BidForm({
  deal,
  displayDate,
  biddingOpen,
  availableCents,
  placeBid,
  onBidAccepted,
}: {
  deal: SellerAuctionDeal;
  displayDate: string;
  biddingOpen: boolean;
  availableCents: number | null;
  placeBid: AuctionPanelProps["placeBid"];
  onBidAccepted: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [status, setStatus] = useState<BidFormStatus>("idle");
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<string | null>(null);
  const [placed, setPlaced] = useState<PlacedBid | null>(null);
  const inFlight = useRef(false);

  const standingCents = deal.bid?.amountCents ?? null;
  const isRaise = standingCents !== null;

  const onChange = useCallback((value: string) => {
    setAmount(value);
    // Editing is the founder starting again: last time's verdict no longer describes what
    // is in the box, and leaving it on screen would make a stale error look current.
    setStatus("idle");
    setFieldError(undefined);
    setFailure(null);
    setPlaced(null);
  }, []);

  if (!biddingOpen) {
    return (
      <p className={styles.shut}>
        Bidding for {formatDisplayDateLong(displayDate)} is closed. The next auction opens
        when that board is finalised.
      </p>
    );
  }

  const parsed = parseBidAmount(amount);
  const typedCents = parsed.ok ? parsed.cents : null;
  // Canon `contracts/API.md`: "a raise charges only the difference". The projection below is
  // this app's arithmetic on core's own figures and is worded as a projection for exactly
  // that reason — core recomputes it, and core's answer is the one that moves credit.
  const additionalCents =
    typedCents === null ? null : Math.max(0, typedCents - (standingCents ?? 0));

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (inFlight.current) return;

    if (!parsed.ok) {
      setStatus("error");
      setFieldError(parsed.message);
      setFailure(null);
      return;
    }

    if (parsed.cents < deal.minimumBidCents) {
      // `minimumBidCents` is core's own number — the auction's daily minimum, or one cent
      // above this deal's standing bid — so saying it here is quoting the contract rather
      // than keeping a second copy of canon § 7's rule.
      setStatus("error");
      setFieldError(
        isRaise
          ? `A standing bid can only be raised. Enter at least ${formatMoney(deal.minimumBidCents)}.`
          : `Tomorrow's auction starts at ${formatMoney(deal.minimumBidCents)}.`,
      );
      setFailure(null);
      return;
    }

    inFlight.current = true;
    setStatus("submitting");
    setFieldError(undefined);
    setFailure(null);
    setPlaced(null);

    void placeBid({ dealId: deal.dealId, amountCents: parsed.cents })
      .then((result) => {
        setPlaced(result);
        setStatus("success");
        setAmount("");
        onBidAccepted();
      })
      .catch((caught: unknown) => {
        const error =
          caught instanceof ApiError
            ? caught
            : new ApiError("Dealbid couldn't place that bid.", { status: null });
        setFailure(messageForBidError(error));
        setStatus("error");
      })
      .finally(() => {
        inFlight.current = false;
      });
  }

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <div className={styles.controls}>
        <AmountField
          label="Your bid"
          context={deal.productName}
          value={amount}
          onChange={onChange}
          disabled={status === "submitting"}
          {...(fieldError === undefined ? {} : { error: fieldError })}
          hint={
            <>
              {isRaise ? "Raise to at least " : "From "}
              <span className="figure">{formatMoney(deal.minimumBidCents)}</span>.{" "}
              {additionalCents === null ? (
                "Credit is reserved while the bid stands and spent only if it wins."
              ) : (
                <BalanceImpact
                  additionalCents={additionalCents}
                  availableCents={availableCents}
                />
              )}
            </>
          }
        />
        <Button type="submit" disabled={status === "submitting"} className={styles.submit}>
          {status === "submitting" ? "Placing…" : isRaise ? "Raise bid" : "Place bid"}
          <VisuallyHidden> for {deal.productName}</VisuallyHidden>
        </Button>
      </div>

      {status === "success" && placed !== null ? (
        <BidAccepted placed={placed} />
      ) : failure === null ? null : (
        <p className={styles.failure} role="alert">
          <VisuallyHidden>Error: </VisuallyHidden>
          {failure}
        </p>
      )}
    </form>
  );
}

/**
 * What this bid would move, stated as a projection.
 *
 * `docs/SECURITY.md` § Credits/bids makes the browser's balance display-only, so this says
 * what *would* happen rather than asserting a new balance. When the projection exceeds what
 * is available it says so plainly — and still lets the bid be sent, because whether the
 * credit is there is core's decision and its refusal is the answer that counts.
 */
function BalanceImpact({
  additionalCents,
  availableCents,
}: {
  additionalCents: number;
  availableCents: number | null;
}) {
  if (availableCents === null) {
    return (
      <>
        Reserves a further <span className="figure">{formatMoney(additionalCents)}</span> of
        promotional credit if accepted.
      </>
    );
  }

  if (additionalCents > availableCents) {
    return (
      <>
        That needs <span className="figure">{formatMoney(additionalCents)}</span>, which is
        more than the <span className="figure">{formatMoney(availableCents)}</span> you have
        available.
      </>
    );
  }

  return (
    <>
      Reserves a further <span className="figure">{formatMoney(additionalCents)}</span>,
      leaving <span className="figure">{formatMoney(availableCents - additionalCents)}</span>{" "}
      available if accepted.
    </>
  );
}

/**
 * The confirmation, in a polite live region.
 *
 * It says four things, and each is one a founder can otherwise get wrong: what the bid now
 * is, where it currently stands, that the exposure it buys is **tomorrow's** display day
 * rather than anything happening now (canon § 7/§ 8), and what the balance did.
 */
function BidAccepted({ placed }: { placed: PlacedBid }) {
  return (
    <div className={styles.accepted} role="status">
      <p className={styles.acceptedTitle}>
        {placed.created ? "Bid placed" : "Bid raised"} —{" "}
        <span className="figure">{formatMoney(placed.amountCents)}</span>, position{" "}
        <span className="figure">{String(placed.projectedRank)}</span> as things stand.
      </p>
      <p>
        {placed.withinTop10
          ? "That is a winning position as things stand. Bidding is still open, so it can change before the board is finalised."
          : "That is not a winning position as things stand. Bidding is still open, so it can still change."}
      </p>
      <p>
        Nothing on today&rsquo;s board changes. If this bid wins, the placement runs for the
        whole of {formatDisplayDateLong(placed.displayDate)}.
      </p>
      <p className={styles.acceptedCredit}>
        <span className="figure">{formatMoney(placed.credit.availableCents)}</span> available
        · <span className="figure">{formatMoney(placed.credit.reservedCents)}</span> reserved.
        Reserved credit returns to available if the bid does not win.
      </p>
    </div>
  );
}
